import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../SleepHub/sleepHubApi';
import { formatMinutes } from './perksHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  toneChip
} from '../../component/common/designSystem';
import { schemeStatusChipClass } from './perksTheme';

/**
 * Perks → User profile.
 *
 * One person's perks life: which schemes they are on and since when, the
 * custom perks they hold and open, and their EAP call record.
 *
 * Reused by the company panel via the `fetcher` / `backPath` / `backLabel`
 * props, following the same shape as the sleep/breathwork/habit/marketplace
 * detail pages.
 *
 * There is deliberately no donut here. Everything this page shows about one
 * person is a small list or a single number — a two-slice donut of one
 * person's two schemes is decoration, not information.
 */
const PerksUserDetail = ({ fetcher, backPath, backLabel }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const enrollments = detail?.enrollments || [];
  const customPerks = detail?.customPerks || [];
  const callsPerDay = detail?.callsPerDay || [];
  const recentCalls = detail?.recentCalls || [];

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'area' },
    colors: [CHART_COLORS.blue],
    stroke: { curve: 'smooth', width: 2 },
    fill: AREA_FILL,
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { show: false },
    xaxis: { categories: lastNDaysLabels(callsPerDay.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} calls` } }
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>User Perks Profile | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.personalBest}
          title="Programmes"
          value={user ? `${user.programmes} of 4` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Live Enrollments"
          value={user ? formatBigNumber(user.activeEnrollments) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.rating}
          title="Custom Perks"
          value={user ? formatBigNumber(user.customPerks) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.plays}
          title="Perk Opens"
          value={user ? formatBigNumber(user.perkClicks) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="EAP Calls"
          value={user ? formatBigNumber(user.supportCalls) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Time on Calls"
          value={user ? formatMinutes(user.totalMinutes) : '-'}
        />
      </div>

      {/* ---------- Scheme enrollments ---------- */}
      <div className="mt-6">
        <Panel
          title="Scheme Enrollments"
          subtitle="Salary Sacrifice and Health Scheme, newest first"
        >
          {enrollments.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Scheme</th>
                  <th className="py-2 pr-4 font-medium">Programme</th>
                  <th className="py-2 pr-4 font-medium">Enrolled</th>
                  <th className="py-2 pr-4 font-medium">Added by</th>
                  <th className="py-2 pr-4 font-medium">Enrollment</th>
                  <th className="py-2 font-medium">Scheme expiry</th>
                </>
              }
            >
              {enrollments.map((row) => (
                <tr key={row.id} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">
                    <div className="font-medium truncate">{row.name}</div>
                    {row.type && <div className="text-xs text-shoorah-gray3">{row.type}</div>}
                  </td>
                  <td className="py-2 pr-4">{row.programme}</td>
                  <td className="py-2 pr-4">{row.enrolledOn || '-'}</td>
                  <td className="py-2 pr-4">{row.enrolledBy || '-'}</td>
                  <td className="py-2 pr-4">
                    {/*
                      The person's enrollment and the scheme's own status are
                      different facts — somebody can have left a scheme that is
                      still running, so both are shown rather than merged.
                    */}
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${schemeStatusChipClass(
                        row.isActive ? 'active' : 'inactive'
                      )}`}
                    >
                      {row.isActive ? 'Live' : 'Ended'}
                    </span>
                  </td>
                  <td className="py-2">
                    {row.expiryDate ? (
                      <span className="text-shoorah-gray3">{row.expiryDate}</span>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>This person is not enrolled on any scheme.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Custom perks ---------- */}
      <div className="mt-6">
        <Panel title="Custom Perks" subtitle="What they hold, and how often they open it">
          {customPerks.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Perk</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Since</th>
                  <th className="py-2 pr-4 font-medium text-right">Opens</th>
                  <th className="py-2 font-medium">Last opened</th>
                </>
              }
            >
              {customPerks.map((perk) => (
                <tr key={perk.id} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4 truncate">{perk.name}</td>
                  <td className="py-2 pr-4">
                    {perk.status ? (
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${schemeStatusChipClass(
                          perk.status
                        )}`}
                      >
                        {perk.status}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-2 pr-4">{perk.enrolledOn || '-'}</td>
                  <td className="py-2 pr-4 text-right font-semibold">{perk.clicks}</td>
                  <td className="py-2">{perk.lastClickedAt || 'Never'}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>This person holds no custom perks.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Support calls ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
        <Panel className="xl:col-span-2" title="Support Calls" subtitle="Per day — last 30 days">
          {callsPerDay.some((value) => value > 0) ? (
            <Chart
              type="area"
              height={300}
              width="100%"
              series={[{ name: 'Calls', data: callsPerDay }]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No support calls in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Recent Calls" subtitle={`Last ${recentCalls.length || 10} logged`}>
          {recentCalls.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 font-medium text-right">Length</th>
                </>
              }
            >
              {recentCalls.map((call) => (
                <tr key={call.id} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{call.date || '-'}</td>
                  <td className="py-2 text-right">{formatMinutes(call.duration)}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No support calls logged.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

PerksUserDetail.propTypes = {
  fetcher: PropTypes.func.isRequired,
  backPath: PropTypes.string.isRequired,
  backLabel: PropTypes.string
};

export default PerksUserDetail;
